import * as React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { isAxiosError } from "axios";
import { LoginForm } from "./LoginForm";
import { type LoginFormInputs, type LoginSteps } from "./types";
import { TwoFactorForm } from "./twoFactorForm";
import { SuccessMessage } from "./successMessage";
import * as analytics from "@/services/analytics";
import * as Sentry from "@sentry/react";
import { useNavigate, useSearchParams } from "react-router";
import { authenticateUser } from "@/views/auth/api";

export const AuthenticationView: React.FC = () => {
  const methods = useForm<LoginFormInputs>();
  const navigate = useNavigate();
  const [error, setError] = React.useState<string | null>(null);
  const [step, setStep] = React.useState<LoginSteps>("creds");
  const [searchParams] = useSearchParams();
  const state = searchParams.get("state");

  const onSubmit = async (payload: LoginFormInputs) => {
    setError(null);

    try {
      const result = await authenticateUser(payload);

      if (result.status === "2fa_required") {
        if (result.user_email) {
          analytics.twoFactorAuthentication(result.user_email);
        }
        setStep("2fa");
      } else if (result.status === "success") {
        analytics.identify(result.user_email || payload.username);
        analytics.login();
        setStep("success");
        if (state) {
          navigate(atob(state));
        } else {
          navigate("/");
        }
      }
    } catch (error) {
      if (isAxiosError(error)) {
        setError((error.response?.data as { detail?: string })?.detail || "Failed to sign in. Please try again.");
      } else {
        Sentry.captureException(error);
        setError("Failed to sign in. Please try again.");
      }
    }
  };

  const handleResendCode = () => {
    void onSubmit(methods.getValues());
  };

  return (
    <form className="space-y-6" onSubmit={methods.handleSubmit(onSubmit)} method="post">
      <FormProvider {...methods}>
        {step === "creds" && <LoginForm error={error} isLoading={methods.formState.isSubmitting} />}
        {step === "2fa" && (
          <TwoFactorForm resendCode={handleResendCode} error={error} isLoading={methods.formState.isSubmitting} />
        )}
        {step === "success" && <SuccessMessage />}
      </FormProvider>
    </form>
  );
};
